// src/components/panels/ThreadPanelHost.jsx
"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import useDiscussionDrawerMode from "@/hooks/useDiscussionDrawerMode";
import { DISCUSSION_DRAWER_MODES } from "@/lib/discussionDrawerModes";
import { DISCUSSION_COMPOSER_TARGETS } from "@/lib/discussionNavigation.mjs";
import { resolveReplyFocus } from "@/lib/replyFocus.mjs";
import { doc, getDoc } from "@/lib/firestore-instrumented";
import { db } from "@/firebase/config";

/**
 * Global host that listens for:
 *   window.dispatchEvent(new CustomEvent("open-thread-panel", { detail: { hubSlug, threadId } }))
 */
export default function ThreadPanelHost() {
  const { mode } = useDiscussionDrawerMode();
  const [open, setOpen] = useState(false);
  const [hubSlug, setHubSlug] = useState("");
  const [threadId, setThreadId] = useState("");
  const [panelRequest, setPanelRequest] = useState(null);
  const requestSequence = useRef(0);
  const ThreadDiscussionPanel = useMemo(
    () =>
      dynamic(() => import("@/components/panels/ThreadDiscussionPanel"), {
        ssr: false,
        loading: () => null,
      }),
    []
  );
  const ThreadBranchPanel = useMemo(
    () =>
      dynamic(() => import("@/components/panels/ThreadBranchPanel"), {
        ssr: false,
        loading: () => null,
      }),
    []
  );
  const ActiveThreadPanel =
    mode === DISCUSSION_DRAWER_MODES.BRANCH ? ThreadBranchPanel : ThreadDiscussionPanel;

  useEffect(() => {
    const onOpen = async (e) => {
      const d = e?.detail || {};
      const nextHubSlug = String(d.hubSlug || "");
      const nextThreadId = String(d.threadId || "");
      const requestId = ++requestSequence.current;
      setHubSlug(nextHubSlug);
      setThreadId(nextThreadId);
      const focusReply = d.focusReply || null;
      const composer = d.openComposer
        ? focusReply
          ? DISCUSSION_COMPOSER_TARGETS.REPLY
          : DISCUSSION_COMPOSER_TARGETS.ROOT
        : null;

      let replyFocus = {
        focusPath: [],
        targetReply: null,
        targetReplyId: "",
      };
      if (!focusReply && d.replyId && nextHubSlug && nextThreadId) {
        try {
          replyFocus = await resolveReplyFocus({
            replyId: d.replyId,
            loadReply: async (replyId) => {
              const snapshot = await getDoc(
                doc(db, "discussions", nextHubSlug, "threads", nextThreadId, "replies", replyId)
              );
              return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
            },
          });
        } catch (error) {
          console.error("[discussion] Could not resolve reply focus", {
            code: error?.code || "unknown",
          });
        }
      }

      if (requestSequence.current !== requestId) return;
      setPanelRequest({
        id: requestId,
        focusReply,
        ...replyFocus,
        composer,
        focusFirstChild: Boolean(d.focusFirstChild),
      });
      setOpen(true);
    };
    window.addEventListener("open-thread-panel", onOpen);
    return () => window.removeEventListener("open-thread-panel", onOpen);
  }, []);

  if (!open) return null;

  return (
    <ActiveThreadPanel
      open={open}
      onClose={() => {
        setOpen(false);
        setPanelRequest(null);
      }}
      hubSlug={hubSlug}
      threadId={threadId}
      panelRequest={panelRequest}
    />
  );
}
